import React, { useEffect, useState, useRef, useCallback } from 'react';
import '../../styles/select-hover.css';
import { usePageTitle } from '../../hooks/usePageTitle';
import { SavedFiltersDropdownRef } from '../../components/good-things/SavedFiltersDropdown';
import { Heart, AlertCircle, Filter, ChevronDown } from 'lucide-react';
import LastSyncIndicator from '../../components/good-things/LastSyncIndicator';
import { GoodThingCard } from '../../components/good-things/GoodThingCard';
import { goodThingsService, clearAllCaches } from '../../services/goodThings';
import { GoodThing } from '../../types/good-things';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import { GoodThingDialog } from '../../components/good-things/GoodThingDialog';
import { GoodThingsGrid } from '../../components/good-things/GoodThingsGrid';
import { GoodThingsLine } from '../../components/good-things/GoodThingsLine';
import { LayoutToggle, LayoutType } from '../../components/good-things/LayoutToggle';
import { Button } from '../../components/ui/button';
import { DatePresetSelector } from '../../components/good-things/DatePresetSelector';
import { SavedFiltersDropdown } from '../../components/good-things/SavedFiltersDropdown';
import { SaveFilterDialog } from '../../components/good-things/SaveFilterDialog';
import { FilterState, DATE_PRESETS } from '../../types/filters';
import { toast } from '../../components/ui/use-toast';
import {
  TooltipProvider,
} from '../../components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

type ErrorType = 'auth' | 'network' | 'data' | 'unknown';

interface ErrorState {
  type: ErrorType;
  message: string;
}

const ITEMS_PER_PAGE = 10;
const ALL_CATEGORIES = 'all';
const ALL_PEOPLE = 'all';

const Feed = () => {
  usePageTitle('Good Thing Tracker');
  const [entries, setEntries] = useState<GoodThing[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<GoodThing[]>([]);
  const [displayedEntries, setDisplayedEntries] = useState<GoodThing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ErrorState | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [layoutType, setLayoutType] = useState<LayoutType>('grid');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [dropdownData, setDropdownData] = useState<{
    categories: string[];
    people: string[];
  }>({
    categories: [],
    people: []
  });
  const [filters, setFilters] = useState<FilterState>({
    dateRange: {
      from: '',
      to: ''
    },
    datePreset: DATE_PRESETS.ALL_DATES,
    category: ALL_CATEGORIES,
    enterer: ALL_PEOPLE,
    recipient: ALL_PEOPLE
  });
  const [saveFilterDialogOpen, setSaveFilterDialogOpen] = useState(false);
  const savedFiltersRef = useRef<SavedFiltersDropdownRef>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Clear cache to ensure we get fresh data
      localStorage.removeItem(FEED_CACHE_KEY);
      
      // Load fresh data from Firebase
      const [entries, dropdownData] = await Promise.all([
        goodThingsService.getAllEntries(),
        goodThingsService.getDropdownData()
      ]);
      
      const sortedData = entries.sort((a, b) => {
        // Try to parse the dates using Date constructor
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        
        // If both dates are valid, compare them
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return bDate.getTime() - aDate.getTime();
        }
        
        // Fallback to string comparison if Date parsing fails
        return String(b.date).localeCompare(String(a.date));
      });
      
      // Update the cache with the new data
      saveFeedCache(sortedData, filters);
      
      setEntries(sortedData);
      setFilteredEntries(sortedData);
      setDropdownData(dropdownData);
    } catch (error: any) {
      
      setError({
        type: 'network',
        message: 'Failed to load entries. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Cache TTL in milliseconds (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000;
  const FEED_CACHE_KEY = 'feedEntriesCache';
  
  // Load cache from localStorage
  const loadFeedCache = () => {
    try {
      const cached = localStorage.getItem(FEED_CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      
    }
    return null;
  };
  
  // Save cache to localStorage with size management
  const saveFeedCache = (data: GoodThing[], filters: FilterState) => {
    try {
      // Use ALL entries instead of limiting to 100
      const cacheData = {
        data: data, // Store ALL entries for full infinite scroll and filtering
        timestamp: Date.now(),
        filters,
        totalCount: data.length
      };
      
      const serialized = JSON.stringify(cacheData);
      
      // Check if the serialized data is too large (>4MB as safety margin)
      if (serialized.length > 4 * 1024 * 1024) {
        // Try to store anyway - modern browsers can handle larger localStorage
        try {
          localStorage.setItem(FEED_CACHE_KEY, serialized);
        } catch (quotaError) {
          // Don't cache but keep all entries in component state
          localStorage.removeItem(FEED_CACHE_KEY);
        }
      } else {
        localStorage.setItem(FEED_CACHE_KEY, serialized);
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        
        // Clear other caches to make space
        localStorage.removeItem('dashboardReceivedCache');
        localStorage.removeItem('dashboardEnteredCache');
        
        try {
          // Try with just the first 25 entries
          const minimalData = data.slice(0, 25);
          const minimalCacheData = {
            data: minimalData,
            timestamp: Date.now(),
            filters,
            totalCount: data.length
          };
          localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(minimalCacheData));
          
        } catch (retryError) {
          
          // Disable caching for this session
          localStorage.removeItem(FEED_CACHE_KEY);
        }
      } else {
        
      }
    }
  };
  
  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    const cached = loadFeedCache();
    if (!cached) {
      return false;
    }
    
    // Check if cache has expired
    const now = Date.now();
    const cacheAge = now - cached.timestamp;
    if (cacheAge > CACHE_TTL) {
      return false;
    }
    
    return true;
  }, [CACHE_TTL]);

  // Load data when component mounts or when user navigates back to it
  useEffect(() => {
    // Always load fresh data to ensure full dataset access
    loadData();
    
    // Add event listener for focus to reload data when user returns to the tab
    // but only if the cache is stale
    const handleFocus = () => {
      if (!isCacheValid()) {
        loadData();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadData, isCacheValid]);

  // Apply filters
  useEffect(() => {
    let filtered = [...entries];

    // Check if we need to apply any filters
    const needsFiltering =
      (filters.dateRange.from && filters.dateRange.to &&
       !(new Date(filters.dateRange.from).getFullYear() === 1970 &&
         new Date(filters.dateRange.to).getFullYear() === new Date().getFullYear())) ||
      (filters.category && filters.category !== ALL_CATEGORIES) ||
      (filters.enterer && filters.enterer !== ALL_PEOPLE) ||
      (filters.recipient && filters.recipient !== ALL_PEOPLE);
    
    if (needsFiltering) {
      // Skip date filtering if ALL_DATES is selected
      if (filters.dateRange.from && 
          !(new Date(filters.dateRange.from).getFullYear() === 1970 && 
            new Date(filters.dateRange.to).getFullYear() === new Date().getFullYear())) {
        // Create a local date object to avoid timezone issues
        const [fromYear, fromMonth, fromDay] = filters.dateRange.from.split('-').map(Number);
        const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
        fromDate.setHours(0, 0, 0, 0);
        
        // Format as YYYY-MM-DD using local components
        const fromDateStr = `${fromYear}-${String(fromMonth).padStart(2, '0')}-${String(fromDay).padStart(2, '0')}`;
        
        filtered = filtered.filter(entry => {
          // Try to parse the date using Date constructor
          const entryDate = new Date(entry.date);
          
          // If date is valid, compare it with fromDate
          if (!isNaN(entryDate.getTime())) {
            entryDate.setHours(0, 0, 0, 0);
            return entryDate >= fromDate;
          }
          
          // Fallback to MM/DD/YY format if Date parsing fails
          try {
            const [month, day, year] = entry.date.split('/');
            if (month && day && year) {
              const formattedDate = `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
              return formattedDate >= fromDateStr;
            }
          } catch (e) {
            
          }
          
          return false;
        });
      }
      
      if (filters.dateRange.to) {
        // Create a local date object to avoid timezone issues
        const [toYear, toMonth, toDay] = filters.dateRange.to.split('-').map(Number);
        const toDate = new Date(toYear, toMonth - 1, toDay);
        toDate.setHours(23, 59, 59, 999);
        
        // Format as YYYY-MM-DD using local components
        const toDateStr = `${toYear}-${String(toMonth).padStart(2, '0')}-${String(toDay).padStart(2, '0')}`;
        
        filtered = filtered.filter(entry => {
          // Try to parse the date using Date constructor
          const entryDate = new Date(entry.date);
          
          // If date is valid, compare it with toDate
          if (!isNaN(entryDate.getTime())) {
            entryDate.setHours(0, 0, 0, 0);
            return entryDate <= toDate;
          }
          
          // Fallback to MM/DD/YY format if Date parsing fails
          try {
            const [month, day, year] = entry.date.split('/');
            if (month && day && year) {
              const formattedDate = `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
              return formattedDate <= toDateStr;
            }
          } catch (e) {
            
          }
          
          return false;
        });
      }

      if (filters.category && filters.category !== ALL_CATEGORIES) {
        filtered = filtered.filter(entry => entry.category === filters.category);
      }

      if (filters.enterer && filters.enterer !== ALL_PEOPLE) {
        filtered = filtered.filter(entry => entry.submitter === filters.enterer);
      }

      if (filters.recipient && filters.recipient !== ALL_PEOPLE) {
        filtered = filtered.filter(entry => entry.recipients.includes(filters.recipient));
      }
    }

    setFilteredEntries(filtered);
    setPage(1); // Reset pagination when filters change
  }, [filters, entries]);

  // Handle pagination
  useEffect(() => {
    const start = 0;
    const end = page * ITEMS_PER_PAGE;
    const newEntries = filteredEntries.slice(start, end);
    setDisplayedEntries(newEntries);
    setHasMore(end < filteredEntries.length);
  }, [page, filteredEntries]);

  // Infinite scroll
  const observer = useRef<IntersectionObserver>();
  const lastEntryRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Get unique categories
  const categories = [...new Set(entries.map(entry => entry.category))].sort();

  const formatName = (email: string): string => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const handleDialogSuccess = () => {
    setDialogOpen(false);
    clearAllCaches(); // Clear caches only when new data is added
    // Also clear the local cache
    localStorage.removeItem(FEED_CACHE_KEY);
    loadData();
  };
  
  // Default filter state
  const defaultFilter: FilterState = {
    dateRange: {
      from: '',
      to: ''
    },
    datePreset: DATE_PRESETS.ALL_DATES,
    category: ALL_CATEGORIES,
    enterer: ALL_PEOPLE,
    recipient: ALL_PEOPLE
  };
  
  // Handle clear filters
  const handleClearFilters = () => {
    setFilters(defaultFilter);
    // Reset the saved filter dropdown selection
    if (savedFiltersRef.current) {
      savedFiltersRef.current.resetSelection();
    }
    toast({
      title: 'Filters cleared',
      description: 'All filters have been reset to default values.',
      variant: 'default'
    });
  };
  
  const handleSaveFilterSuccess = () => {
    setSaveFilterDialogOpen(false);
    // Refresh the saved filters list
    if (savedFiltersRef.current) {
      savedFiltersRef.current.loadSavedFilters();
    }
  };

  if (error) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const hasActiveFilters = (filters.category && filters.category !== ALL_CATEGORIES) || 
    (filters.enterer && filters.enterer !== ALL_PEOPLE) || 
    (filters.recipient && filters.recipient !== ALL_PEOPLE) || 
    filters.dateRange.from || 
    filters.dateRange.to;

  return (
    <TooltipProvider>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Feed</h1>
            <p className="text-gray-500 dark:text-gray-400">Browse and filter Good Things</p>
            <LastSyncIndicator />
          </div>
          <div className="flex items-center gap-2">
            {/* Only show layout toggle on desktop */}
            <div className="hidden md:block">
              <LayoutToggle 
                onLayoutChange={setLayoutType} 
                className="mr-2"
              />
            </div>
            <GoodThingDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              onSuccess={handleDialogSuccess}
            />
          </div>
        </div>

        {/* Filters */}
        <Collapsible
          open={isFiltersOpen}
          onOpenChange={setIsFiltersOpen}
          className="mb-8 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700"
        >
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <span className="font-medium dark:text-white">
                Filters {hasActiveFilters && `(${
                  ((filters.category && filters.category !== ALL_CATEGORIES) ? 1 : 0) + 
                  ((filters.enterer && filters.enterer !== ALL_PEOPLE) ? 1 : 0) + 
                  ((filters.recipient && filters.recipient !== ALL_PEOPLE) ? 1 : 0) + 
                  (filters.dateRange.from ? 1 : 0) + 
                  (filters.dateRange.to ? 1 : 0)
                })`}
              </span>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isFiltersOpen ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="p-4 border-t dark:border-gray-700 space-y-6">
            {/* First Row - Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* From Dropdown */}
              <div>
                <h3 className="text-sm font-medium mb-2 dark:text-white">From</h3>
                <Select
                  value={filters.enterer}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, enterer: value }))}
                >
                  <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <SelectValue placeholder="Select a person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PEOPLE}>All People</SelectItem>
                    {dropdownData.people
                      .filter(person => person && person.trim() !== '')
                      .map(person => (
                        <SelectItem key={`enterer-${person}`} value={person}>
                          {formatName(person)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* To Dropdown */}
              <div>
                <h3 className="text-sm font-medium mb-2 dark:text-white">To</h3>
                <Select
                  value={filters.recipient}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, recipient: value }))}
                >
                  <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <SelectValue placeholder="Select a person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PEOPLE}>All People</SelectItem>
                    {dropdownData.people
                      .filter(person => person && person.trim() !== '')
                      .map(person => (
                        <SelectItem key={`recipient-${person}`} value={person}>
                          {formatName(person)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category Dropdown */}
              <div>
                <h3 className="text-sm font-medium mb-2 dark:text-white">Category</h3>
                <Select
                  value={filters.category}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_CATEGORIES}>All Categories</SelectItem>
                    {categories
                      .filter(category => category && category.trim() !== '')
                      .map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Preset Selector */}
              <div>
                <DatePresetSelector
                  fromDate={filters.dateRange.from}
                  toDate={filters.dateRange.to}
                  onDateRangeChange={(from, to, preset) => {
                    setFilters(prev => ({
                      ...prev,
                      dateRange: { from, to },
                      datePreset: preset
                    }));
                  }}
                />
              </div>
            </div>
            
            {/* Second Row - Filter Management (Centered) */}
            <div className="flex justify-center mt-4">
              <div className="flex flex-col md:flex-row gap-4 items-end max-w-3xl w-full">
                {/* Saved Filters Dropdown */}
                <div className="md:w-96 w-full">
                  <SavedFiltersDropdown
                    ref={savedFiltersRef}
                    onFilterSelect={setFilters}
                    currentFilter={filters}
                    onSaveFilter={() => setSaveFilterDialogOpen(true)}
                    className="w-full"
                  />
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 mt-2 md:mt-0">
                  <Button 
                    variant="default" 
                    size="sm"
                    onClick={() => setSaveFilterDialogOpen(true)}
                    className="bg-pink-600 hover:bg-pink-700 dark:bg-pink-600 dark:hover:bg-pink-700"
                  >
                    Save Current...
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters}
                    className="dark:text-white dark:border-gray-600"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Save Filter Dialog */}
        <SaveFilterDialog
          open={saveFilterDialogOpen}
          onOpenChange={setSaveFilterDialogOpen}
          currentFilter={filters}
          onSuccess={handleSaveFilterSuccess}
        />

        {/* Entries */}
        {layoutType === 'grid' ? (
          <GoodThingsGrid variant="compact">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700 animate-pulse">
                <div className="flex justify-between items-start mb-4">
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-16 w-full mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-32 rounded-full" />
                  <Skeleton className="h-8 w-48 rounded-full" />
                </div>
              </div>
            ))
          ) : displayedEntries.length > 0 ? (
            displayedEntries.map((entry, index) => (
              <div
                key={entry.id}
                ref={index === displayedEntries.length - 1 ? lastEntryRef : undefined}
                className="h-fit"
              >
                <GoodThingCard 
                  goodThing={entry} 
                  onEdit={() => loadData()}
                  onDelete={() => loadData()}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-12 col-span-2">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900 mb-4">
                <Heart className="h-8 w-8 text-pink-500 dark:text-pink-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No entries found</h3>
              <p className="text-gray-500 dark:text-gray-400">Try adjusting your filters</p>
            </div>
          )}
          </GoodThingsGrid>
        ) : (
          <GoodThingsLine variant="compact">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700 animate-pulse">
                <div className="flex justify-between items-start mb-4">
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-16 w-full mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-32 rounded-full" />
                  <Skeleton className="h-8 w-48 rounded-full" />
                </div>
              </div>
            ))
          ) : displayedEntries.length > 0 ? (
            displayedEntries.map((entry, index) => (
              <div
                key={entry.id}
                ref={index === displayedEntries.length - 1 ? lastEntryRef : undefined}
                className="h-fit"
              >
                <GoodThingCard 
                  goodThing={entry} 
                  onEdit={() => loadData()}
                  onDelete={() => loadData()}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900 mb-4">
                <Heart className="h-8 w-8 text-pink-500 dark:text-pink-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No entries found</h3>
              <p className="text-gray-500 dark:text-gray-400">Try adjusting your filters</p>
            </div>
          )}
          </GoodThingsLine>
        )}
      </div>
    </TooltipProvider>
  );
};

export default Feed;
